import axios from 'axios';
import { navigate as routerNavigate } from '@reach/router';
import AppState from 'js/stores/AppState';

/**
 * Allows tagging of a Reach Link component as active based on the current url
 * https://reach.tech/router/api/Link
 */
export const isPartiallyActive = ({ isPartiallyCurrent }) => (isPartiallyCurrent ? { className: 'active' } : null);
export const isActive = ({ isCurrent }) => (isCurrent ? { className: 'active' } : null);

/**
 * Issue an axios request
 *
 * @export
 * @param {string} endpoint
 * @param {object} properties
 * @param {object} data
 * @returns {object}
 */
export function makeRequest(endpoint, properties = {}, data = null, options = {}) {
  const route = parseRoute(window.app.endpoints[endpoint] || endpoint);
  const { method } = route;
  const url = composeUrl(properties, route).replace(/\/(undefined|null)/g, '');

  var queryString = null;
  if (method.toLowerCase() === 'get' && data && typeof data === 'object' && Object.keys(data).length > 0) {
    queryString = Object.keys(data)
      .map(key => key + '=' + encodeURIComponent(data[key]))
      .join('&');
  }

  const request = axios(
    Object.assign(
      {
        method: method.toLowerCase(),
        url: queryString ? `${url}?${queryString}` : url,
        data: data,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-TOKEN': window.app.csrf_token,
          // 'Authorization': `Bearer ${(window.app.currentUser.accessToken ? window.app.currentUser.accessToken : window.app.accessToken)}`
        },
      },
      options,
    ),
  );
  request.catch(error => error && error.response && error.response.status === 419 && AppState.invalidateSession());

  return request;
}

export function navigate(destination, options) {
  routerNavigate(destination, options);
}

export function errorResponse(error) {
  return error && error.response
    ? error.response.data
      ? error.response.data.message || error.response.statusText
      : error.response.statusText
    : (() => {
        console.error('Unknown error', error);
        return 'Unknown error occurred';
      })();
}

export function pagePath(endpoint) {
  if (!window.app.endpoints[endpoint]) {
    throw `pagePath(${endpoint}) not found`;
  }
  return window.app.endpoints[endpoint].split(' ')[1].replace(/:[a-zA-Z0-9_]+\?$/, '*');
}

export function reportClientError(details) {
  makeRequest('client-error', null, details);
}

/**
 * Return the rendered url for an endpoint
 *
 * @export
 * @param {any} endpoint
 * @param {object} endpointProperties
 * @returns
 */
export function url(endpoint, endpointProperties, parameters) {
  let queryString = '';
  if (parameters) {
    queryString = Object.keys(parameters)
      .map(key => key + '=' + encodeURIComponent(parameters[key]))
      .join('&');
  }

  const route = parseRoute(window.app.endpoints[endpoint] || endpoint);
  const renderedUrl = composeUrl(endpointProperties, route).replace(/\/(undefined|null)/g, '');
  return `${renderedUrl}${queryString ? `?${queryString}` : ''}`;
}
const pathParams = /(:([a-z_A-Z\?]+))/gi;
const paramReducer = /[:?]/g;

/**
 * Take a route and Model and generate the resolved url for the routes CRUD action
 *
 * @export
 * @param {Model} model
 * @param {object} route
 * @returns {string}
 */
function composeUrl(properties, route) {
  const { path, params } = route;

  if (!properties || typeof properties !== 'object' || Object.keys(properties).length === 0) {
    return path;
  }

  return (params || []).reduce((url, param) => {
    url = url.replace(param.urlParam, properties[param.field]);
    return url;
  }, path);
}

/**
 * Split an individual verb route string into its constituent parts
 *
 * @param string route
 * @returns
 */
function parseRoute(route = null) {
  if (!route) {
    throw 'No route provided for Model';
  }

  const [method, path] = route.split(' ');
  const params = reduceParams((path || method).match(pathParams));

  if (!path) {
    return {
      method: 'GET',
      path: method,
      params: params,
    };
  }

  return {
    method: method,
    path: path,
    params: params,
  };
}

/**
 * Reduce list of route parameters into a form we can use to replace the values with later
 *
 * @param array params
 * @returns
 */
function reduceParams(params) {
  return !params
    ? params
    : params.map(param => {
        return {
          urlParam: param,
          field: param.replace(paramReducer, ''),
        };
      });
}
